import React from 'react';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import {
  buildNodeQuickCreateHandlers,
  useWorkflowHydrationGuard,
  type WorkflowNodeType,
  type QuickCreateTarget,
  type BranchType,
} from '../helpers';
import type { UndoHistoryHandle } from './useUndoHistory';
import type { VersionSelection } from '../VersionDropdown';
import type {
  DraftSnapshotDetail,
  SnapshotDetail,
  WorkflowRevisionDetail,
} from './useWorkflowSnapshots';
import { snapshotToWorkflowNodes } from './snapshotNodes';
import { resolvePlaybookStepCount } from '../utils/resolvePlaybookStepCount';

type WorkflowDetailNode = {
  id: string;
  nodeType: string;
  configJson?: Record<string, unknown> | null;
  hasCachedReplay?: boolean | null;
  cachedAt?: string | null;
  cacheRequiresAgent?: boolean | null;
  cachePlaybookStepCount?: number | null;
};

type WorkflowDetailEdge = {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType?: string | null;
};

type WorkflowDetail = {
  updatedAt?: string | number | null;
  snapshotVersion?: number | null;
  setupFlowPreviews?: Record<string, unknown> | null;
  nodes?: WorkflowDetailNode[] | null;
  edges?: WorkflowDetailEdge[] | null;
};

type UseCanvasHydrationParams = {
  workflowId: string | null;
  workflowDetailQ: {
    data: WorkflowDetail | null | undefined;
    isLoading: boolean;
  };
  previewingVersion: VersionSelection | null;
  previewSnapshotQ: {
    data: SnapshotDetail | null | undefined;
  };
  previewRevisionQ: {
    data: WorkflowRevisionDetail | null | undefined;
  };
  previewDraftSnapshotQ: {
    data: DraftSnapshotDetail | null | undefined;
  };
  nodes: WorkflowNode[];
  setNodes: (nodes: WorkflowNode[] | ((prev: WorkflowNode[]) => WorkflowNode[])) => void;
  setEdges: (edges: WorkflowEdge[] | ((prev: WorkflowEdge[]) => WorkflowEdge[])) => void;
  getNodes: () => WorkflowNode[];
  selectedNodeForConfig: WorkflowNode | null;
  setSelectedNodeForConfig: (node: WorkflowNode | null) => void;
  activeSidebarTab: string;
  exitConfigTab: () => void;
  handleNodeConfig: (node: WorkflowNode) => void;
  /** Toggle a node's `configJson.deactivated` (deactivated nodes are skipped during runs). */
  onToggleNodeDeactivated: (nodeId: string, next: boolean) => void;
  handleQuickCreateNode: (sourceNodeId: string, nodeType: QuickCreateTarget) => void;
  handleBranchQuickCreate: (
    sourceNodeId: string,
    nodeType: QuickCreateTarget,
    branchType: BranchType
  ) => void;
  setCachePopover: (popover: { nodeId: string; position: { x: number; y: number } } | null) => void;
  setIsPlaybookOpen: (open: boolean) => void;
  setPlaybookScrollToNodeId: (nodeId: string | null) => void;
  hasApiCatalog: boolean;
  hasSandboxActions: boolean;
  hasMcp: boolean;
  undoHistory: UndoHistoryHandle;
  autoFocusedWorkflowRef: React.MutableRefObject<string | null>;
  setPreviewingVersion: (v: UseCanvasHydrationParams['previewingVersion']) => void;
  draftStateRef: React.MutableRefObject<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] } | null>;
  /**
   * While the user is suggesting changes the canvas holds staged-only edits that
   * never hit the server. Never re-hydrate from the live workflow in that state
   * — a concurrent refetch would clobber the in-progress suggestion.
   */
  isSuggesting: boolean;
};

export function useCanvasHydration({
  workflowId,
  workflowDetailQ,
  previewingVersion,
  previewSnapshotQ,
  previewRevisionQ,
  previewDraftSnapshotQ,
  nodes,
  setNodes,
  setEdges,
  getNodes: _getNodes,
  selectedNodeForConfig,
  setSelectedNodeForConfig,
  activeSidebarTab,
  exitConfigTab,
  handleNodeConfig,
  onToggleNodeDeactivated,
  handleQuickCreateNode,
  handleBranchQuickCreate,
  setCachePopover,
  setIsPlaybookOpen,
  setPlaybookScrollToNodeId,
  hasApiCatalog,
  hasSandboxActions,
  hasMcp,
  undoHistory,
  autoFocusedWorkflowRef,
  setPreviewingVersion,
  draftStateRef,
  isSuggesting,
}: UseCanvasHydrationParams) {
  const { shouldHydrate, resetHydration } = useWorkflowHydrationGuard();

  // Hydrate workflow data into canvas nodes/edges
  React.useEffect(() => {
    if (!workflowId || !workflowDetailQ.data) {
      return;
    }

    // Suggest mode owns the canvas — staged edits must survive refetches.
    if (isSuggesting) {
      return;
    }

    const workflow: WorkflowDetail = workflowDetailQ.data;
    const workflowVersion =
      typeof workflow.updatedAt === 'string' || typeof workflow.updatedAt === 'number'
        ? workflow.updatedAt
        : typeof workflow.snapshotVersion === 'number'
          ? workflow.snapshotVersion
          : null;
    const setupFlowPreviews = workflow.setupFlowPreviews ?? {};

    if (!shouldHydrate(workflowId, workflowVersion)) {
      return;
    }

    const hydratedNodes: WorkflowNode[] = (workflow.nodes ?? []).map((node) => {
      const nodeData = node.configJson || {};
      const baseNode = {
        id: node.id,
        type: node.nodeType,
      };

      const data: Record<string, any> = {
        ...nodeData,
        executionStatus: 'idle',
        executionMessage: '',
        isExecuting: false,
        isPersisted: true,
        hasCachedReplay: node.hasCachedReplay ?? false,
        cachedAt: node.cachedAt ?? null,
        cacheRequiresAgent: node.cacheRequiresAgent ?? false,
        // The "N steps" badge counts the node's playbook. Under playbook-v3 the
        // playbook lives on the cache row (config_json is FROZEN, not cleared),
        // so the row count must win over any stale configJson steps a migrated
        // node still carries — while a legacy v2 row with no playbook_json falls
        // back to configJson. resolvePlaybookStepCount encodes that precedence
        // (same "row wins when non-empty" rule as resolveCachePlaybookNodes).
        playbookStepCount: resolvePlaybookStepCount({
          configPlaybookSteps: nodeData.playbookSteps,
          cachePlaybookStepCount: node.cachePlaybookStepCount,
        }),
        inlineAssertionCount: Array.isArray(nodeData.inlineAssertions)
          ? nodeData.inlineAssertions.length
          : undefined,
        onPlaybookClick: () => {
          setPlaybookScrollToNodeId(node.id);
          setIsPlaybookOpen(true);
        },
        onCacheClick: (event: React.MouseEvent) => {
          setCachePopover({ nodeId: node.id, position: { x: event.clientX, y: event.clientY } });
        },
        onConfigClick: () =>
          handleNodeConfig({
            id: baseNode.id,
            type: baseNode.type,
            data: nodeData,
          }),
      };

      // Deactivate/reactivate toggle. Condition nodes are excluded: skipping
      // one at run time would leave both branches live, which is never what
      // the author meant (validation also rejects it).
      if (baseNode.type !== 'condition' && baseNode.type !== 'end') {
        data.onToggleDeactivate = () =>
          onToggleNodeDeactivated(node.id, nodeData.deactivated !== true);
      }

      const seedWorkflowId = nodeData.seedWorkflowId;
      if (baseNode.type === 'seed' && typeof seedWorkflowId === 'string' && seedWorkflowId) {
        data.onViewFlow = () =>
          window.open(`/flows/${seedWorkflowId}`, '_blank', 'noopener,noreferrer');
      }

      // Same affordance for `call_flow`, which names its child `flowId` rather
      // than `seedWorkflowId`. `CallFlowNode` gates its "View called flow"
      // button on this callback, so omitting the arm renders it never.
      const callFlowId = nodeData.flowId;
      if (baseNode.type === 'call_flow' && typeof callFlowId === 'string' && callFlowId) {
        data.onViewFlow = () =>
          window.open(`/flows/${callFlowId}`, '_blank', 'noopener,noreferrer');
      }

      if (baseNode.type === 'setup') {
        const setupFlowId = nodeData.setupFlowId;
        const snapshotVersion = nodeData.snapshotVersion ?? null;
        if (typeof setupFlowId === 'string') {
          data.setupFlowPreview = setupFlowPreviews[`${setupFlowId}:${snapshotVersion}`] ?? null;
        } else {
          data.setupFlowPreview = null;
        }
      }

      Object.assign(
        data,
        buildNodeQuickCreateHandlers({
          nodeType: baseNode.type as WorkflowNodeType,
          nodeId: baseNode.id,
          allowTemp: true,
          handleQuickCreate: handleQuickCreateNode,
          handleBranchCreate: handleBranchQuickCreate,
          enableApiSequence: hasApiCatalog,
          enableSandboxAction: hasSandboxActions,
          enableMcp: hasMcp,
        })
      );

      if (baseNode.type === 'condition') {
        data.enableApiSequence = hasApiCatalog;
      }

      return {
        ...baseNode,
        data,
      };
    });

    const hydratedEdges: WorkflowEdge[] = (workflow.edges ?? []).map((edge) => ({
      id: edge.id,
      source: edge.sourceNodeId,
      target: edge.targetNodeId,
      type: edge.edgeType || 'default',
    }));

    setNodes(hydratedNodes);
    setEdges(hydratedEdges);
    undoHistory.clearHistory();
  }, [
    workflowDetailQ.data,
    workflowId,
    isSuggesting,
    setNodes,
    setEdges,
    handleNodeConfig,
    onToggleNodeDeactivated,
    handleQuickCreateNode,
    handleBranchQuickCreate,
    shouldHydrate,
  ]);

  // Reset state when workflowId is cleared
  React.useEffect(() => {
    if (!workflowId) {
      setNodes([]);
      setEdges([]);
      setSelectedNodeForConfig(null);
      autoFocusedWorkflowRef.current = null;
      resetHydration();
      setPreviewingVersion(null);
      draftStateRef.current = null;
      undoHistory.clearHistory();
    }
  }, [workflowId, setNodes, setEdges, setSelectedNodeForConfig, resetHydration, undoHistory]);

  // Load snapshot nodes/edges when previewing a historical version (published or draft)
  React.useEffect(() => {
    if (!previewingVersion) return;

    const snapshotData =
      previewingVersion.type === 'snapshot'
        ? previewSnapshotQ.data
        : previewingVersion.type === 'draft-snapshot'
          ? previewDraftSnapshotQ.data
          : previewRevisionQ.data;

    if (!snapshotData?.snapshotJson) return;

    const snapshot = snapshotData.snapshotJson;
    const snapshotNodes = snapshot.nodes;
    const snapshotEdges = snapshot.edges;
    const setupFlowPreviews = snapshotData.setupFlowPreviews ?? {};

    const previewNodes: WorkflowNode[] = snapshotToWorkflowNodes(snapshotNodes).map((node) => {
      const data: Record<string, any> = {
        ...node.data,
        executionStatus: 'idle',
        executionMessage: '',
        isExecuting: false,
        isPersisted: true,
      };

      if (node.type === 'setup') {
        const setupFlowId = data?.setupFlowId;
        const snapshotVersion = data?.snapshotVersion ?? null;
        if (typeof setupFlowId === 'string') {
          data.setupFlowPreview = setupFlowPreviews[`${setupFlowId}:${snapshotVersion}`] ?? null;
        } else {
          data.setupFlowPreview = null;
        }
      }

      return { ...node, data };
    });

    const previewEdges: WorkflowEdge[] = snapshotEdges.flatMap((rawEdge): WorkflowEdge[] => {
      if (typeof rawEdge !== 'object' || rawEdge === null || Array.isArray(rawEdge)) return [];
      const edge = rawEdge as Record<string, unknown>;
      if (typeof edge.id !== 'string') return [];
      const source = edge.sourceNodeId ?? edge.source;
      const target = edge.targetNodeId ?? edge.target;
      if (typeof source !== 'string' || typeof target !== 'string') return [];
      return [
        {
          id: edge.id,
          source,
          target,
          type: typeof edge.edgeType === 'string'
            ? edge.edgeType
            : typeof edge.type === 'string'
              ? edge.type
              : 'default',
        },
      ];
    });

    setNodes(previewNodes);
    setEdges(previewEdges);
    setSelectedNodeForConfig(null);
  }, [
    previewingVersion,
    previewSnapshotQ.data,
    previewRevisionQ.data,
    previewDraftSnapshotQ.data,
    setNodes,
    setEdges,
  ]);

  // Keep selected node in sync with latest node state
  React.useEffect(() => {
    if (!selectedNodeForConfig) {
      return;
    }
    const latestNode = nodes.find((node) => node.id === selectedNodeForConfig.id);
    if (!latestNode) {
      setSelectedNodeForConfig(null);
      if (activeSidebarTab === 'config') {
        exitConfigTab();
      }
      return;
    }
    if (latestNode !== selectedNodeForConfig) {
      setSelectedNodeForConfig(latestNode);
    }
  }, [nodes, selectedNodeForConfig, activeSidebarTab, exitConfigTab, setSelectedNodeForConfig]);

  return { resetHydration };
}
